import * as React from "react";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { IconButton, Box, CircularProgress, Button, Typography, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { theme } from "app/theme";
import { fetchAllDataFromAPI } from "../api/dataGridApiService";

interface CustomToolbarProps {
  preferance?: boolean;
  filterable?: boolean;
  exportable?: boolean;
  exportFileName?: string;
  exportColumns?: string[];
  editable?: boolean;
  isEditEnabled?: boolean;
  handleEditClick?: () => void;
  handleSaveClick?: () => void;
  handleCancelClick?: () => void;
  apiEndpoint: string;
  params: any;
  onFilterClick?: () => void;
  showKeyboardShortcuts?: boolean;
}

export const CustomToolbar: React.FC<CustomToolbarProps> = ({
  preferance,
  filterable,
  exportable,
  exportFileName,
  exportColumns = [],
  editable,
  isEditEnabled,
  handleEditClick,
  handleSaveClick,
  handleCancelClick,
  apiEndpoint,
  params,
  onFilterClick,
  showKeyboardShortcuts = false,
}) => {
  const apiRef = useGridApiContext();
  const [isExporting, setIsExporting] = React.useState(false);





  const convertToCSV = (data) => {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj => 
      Object.values(obj).map(value => 
        `"${String(value).replace(/"/g, '""')}"`
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };
  
  const downloadCSV = (content, fileName) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFullExport = async () => {
    setIsExporting(true);
    try {
      const response = await fetchAllDataFromAPI(apiEndpoint, params);
      // console.log(' csv: ' , response);
      // return;
      if(!response?.body && !response?.body?.data && !Array.isArray(response?.body?.data)) {
        return;
      }

      // Create a mapping from original keys to display names
      const keyToDisplayNameMap = {};
      if (response.body.headers && Array.isArray(response.body.headers)) {
        response.body.headers.forEach(header => {
          if (header.display_name) {
            keyToDisplayNameMap[header.db_field] = header.display_name;
          }
        });
      }

      const dataWithDisplayNames = response.body.data.map(item => {
        const newItem = {};
        // Use the order from headers array to maintain column order
        response.body.headers.forEach(header => {
          if (header.display_name && item.hasOwnProperty(header.db_field)) {
            newItem[header.display_name] = item[header.db_field];
          }
        });
        return newItem;
      });
      const csvContent = convertToCSV(dataWithDisplayNames);
      
      downloadCSV(csvContent, `${exportFileName}.csv` || 'data-export.csv');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {/* Left side - Toolbar buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {preferance && (
          <GridToolbarColumnsButton
            onResize={undefined}
            nonce={undefined}
            onResizeCapture={undefined}
          />
        )}
        {filterable && onFilterClick && (
          <Button
            className="filter-button"
            onClick={onFilterClick}
            startIcon={<FilterListIcon />}
            variant="text"
            size="small"
            aria-label="Open filter panel. Press F for keyboard shortcut."
            sx={{ 
              textTransform: 'none', 
              fontWeight: 500,
              color: 'primary.main',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '0.875rem',
              minWidth: 'auto',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(255, 102, 0, 0.1)',
                transform: 'scale(1.02)',
              },
            }}
          >
            Filter
          </Button>
        )}
        {filterable && !onFilterClick && (
          <GridToolbarFilterButton
            onResize={undefined}
            nonce={undefined}
            onResizeCapture={undefined}
          />
        )}
        <GridToolbarDensitySelector
          onResize={undefined}
          nonce={undefined}
          onResizeCapture={undefined}
        />
        {!!exportable && (
          <Button
            onClick={handleFullExport}
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            variant="text"
            size="small"
            aria-label="Export data. Press E for keyboard shortcut."
            sx={{ 
              textTransform: 'none', 
              fontWeight: 500,
              color: 'primary.main',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '0.875rem',
              minWidth: 'auto',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(255, 102, 0, 0.1)',
                transform: 'scale(1.02)',
              },
              '&:disabled': {
                color: 'rgba(0, 0, 0, 0.38)',
                transform: 'none',
              },
            }}
          >
            Export
          </Button>
        )}
      </Box>

      {/* Right side - Keyboard shortcuts and edit buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Keyboard shortcuts */}
        {showKeyboardShortcuts && (filterable || exportable) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KeyboardIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
              Keyboard shortcuts:
            </Typography>
            {filterable && (
              <Chip
                label="F - Filter"
                size="small"
                variant="outlined"
                sx={{ 
                  fontSize: '0.7rem', 
                  height: '20px',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}
              />
            )}
            {exportable && (
              <Chip
                label="E - Export"
                size="small"
                variant="outlined"
                sx={{ 
                  fontSize: '0.7rem', 
                  height: '20px',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}
              />
            )}
            <Chip
              label="Esc - Close/Reset"
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.7rem', 
                height: '20px',
                borderColor: 'text.secondary',
                color: 'text.secondary',
              }}
            />
          </Box>
        )}
        
        {/* Edit buttons */}
        {editable && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {!isEditEnabled ? (
              <IconButton onClick={handleEditClick} color="primary">
                <EditIcon />
              </IconButton>
            ) : (
              <>
                <IconButton onClick={handleSaveClick} color="success">
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={handleCancelClick} color="error">
                  <CancelIcon />
                </IconButton>
              </>
            )}
          </Box>
        )}
      </Box>
    </GridToolbarContainer>
  );
};
